<template>
    <fieldset>
        <legend v-if="label" class="sr-only">{{ label }}</legend>
        <RadioGroupRoot
            :model-value="modelValue?.toString()"
            @update:model-value="(v) => emit('update:modelValue', Number(v))"
            @keydown.capture="handleKeydown"
            @click.capture="handleClick">
            <div class="flex items-center justify-between gap-2">
                <RadioGroupItem
                    v-for="point in SCALE_POINTS"
                    :key="point.value"
                    :value="point.value.toString()"
                    :disabled="disabled"
                    :aria-label="point.label()"
                    :class="[
                        'size-8 rounded-full border-2 transition-[background-color,box-shadow,scale] duration-(--motion-duration-fast) motion-reduce:transition-[background-color,box-shadow] cursor-pointer outline-none',
                        'focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-control-accent',
                        modelValue === point.value ? `${point.border} ${point.bg}` : `${point.border} bg-transparent motion-safe:hover:scale-110`,
                    ]" />
            </div>
        </RadioGroupRoot>
        <div class="flex justify-between mt-2">
            <span class="details text-ink-muted">{{ t("components.likertScale.disagree") }}</span>
            <span class="details text-ink-muted">{{ t("components.likertScale.agree") }}</span>
        </div>
    </fieldset>
</template>

<script lang="ts">
import { i18n } from "~vue/i18n";

export interface Props {
    modelValue?: number;
    disabled?: boolean;
    label?: string;
}

/** Exported so a caller can enumerate the valid answers rather than restating the scale. */
export const SCALE_POINTS = [
    { value: -3, label: () => i18n.global.t("components.likertScale.stronglyDisagree"), border: "border-ink", bg: "bg-ink" },
    { value: -2, label: () => i18n.global.t("components.likertScale.disagree"), border: "border-ink", bg: "bg-ink" },
    { value: -1, label: () => i18n.global.t("components.likertScale.slightlyDisagree"), border: "border-ink-muted", bg: "bg-ink-muted" },
    { value: 0, label: () => i18n.global.t("components.likertScale.neutral"), border: "border-ink-muted", bg: "bg-ink-muted" },
    { value: 1, label: () => i18n.global.t("components.likertScale.slightlyAgree"), border: "border-ink-muted", bg: "bg-ink-muted" },
    { value: 2, label: () => i18n.global.t("components.likertScale.agree"), border: "border-ink", bg: "bg-ink" },
    { value: 3, label: () => i18n.global.t("components.likertScale.stronglyAgree"), border: "border-ink", bg: "bg-ink" },
] as const;
</script>

<script setup lang="ts">
import { RadioGroupItem, RadioGroupRoot } from "reka-ui";
import { nextTick } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

defineProps<Props>();

const emit = defineEmits<{
    "update:modelValue": [value: number];
    advance: [columnIndex: number];
}>();

function handleKeydown(event: KeyboardEvent) {
    if (event.key === "Enter" || event.key === " ") {
        const target = event.target as HTMLElement;
        if (target.getAttribute("role") === "radio") {
            event.preventDefault();
            event.stopPropagation();
            const value = target.getAttribute("value");
            if (value === null) {
                return;
            }
            emit("update:modelValue", Number(value));
            const columnIndex = SCALE_POINTS.findIndex((p) => p.value === Number(value));
            nextTick(() => emit("advance", columnIndex >= 0 ? columnIndex : 0));
        }
    }
}

function handleClick(event: MouseEvent) {
    // reka-ui fires a programmatic click on arrow key, this blocks unless its from a user agent
    if (!event.isTrusted) {
        return;
    }
    const target = (event.target as HTMLElement).closest('[role="radio"]');
    if (!target) {
        return;
    }
    const value = target.getAttribute("value");
    if (value === null) {
        return;
    }
    const columnIndex = SCALE_POINTS.findIndex((p) => p.value === Number(value));
    nextTick(() => emit("advance", columnIndex >= 0 ? columnIndex : 0));
}
</script>
